import { BankOutlined, SearchOutlined } from "@ant-design/icons";
import { Empty, Input, Skeleton, Table, Tooltip, Progress } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getPortfolioLink } from "../../assets/api/portfolio";
import { getUserType } from "../../assets/helpers";
import boa from "../../assets/Images/bankLogo/BOA.svg";
import citi from "../../assets/Images/bankLogo/reliance.svg";
import pnb from "../../assets/Images/bankLogo/PNB.svg";
import reliance from "../../assets/Images/bankLogo/reliance.svg";
import sbi from "../../assets/Images/bankLogo/citi.svg";
import usbank from "../../assets/Images/bankLogo/USBank.svg";
import { fetchBankDashboard } from "../../store/portfolioSlice";
import { fetchTelecallerBankDashboard } from "../../store/telecaller/telecallerDashboard";
import "./BankDashboardPage.scss";
import PortfolioOverview from "./components/PortfolioOverview";
import EllipsisMenu from "../../Reuseable/EllipsisMenu/EllipsisMenu";
import { ReactComponent as Eye } from "../../assets/Images/eye.svg";
import { ReactComponent as Download } from "../../assets/Images/download.svg";
import { formatCurrency, formatWithCommas } from "../../assets/helpers";

const logos = {
  "HDFC RARC": reliance,
  "Citi Bank": citi,
  "State Bank of India": sbi,
  "Punjab National Bank": pnb,
  "Bank Of America": boa,
  "US Bank": usbank,
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const BankDashboardPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { token, user } = useSelector((state) => state.auth);
  const { bankDashboard, bankDashboard_loading } = useSelector(
    (state) => state?.portfolio,
  );
  const { tele_bank_dashboard_loading, tele_bank_dashboard } = useSelector(
    (state) => state.telecallerDashboard,
  );
  const { format } = useSelector((state) => state.priceFormat);

  const [downloadLoading, setDownloadLoading] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchBankDashboard());
    dispatch(fetchTelecallerBankDashboard());
  }, [dispatch]);

  const userType = getUserType(user);

  const getBankDashboard = (type) => {
    switch (type) {
      case "telecaller":
        return {
          data: tele_bank_dashboard,
          loading: tele_bank_dashboard_loading,
        };
      case "supervisor":
      case "agency":
        return { data: bankDashboard, loading: bankDashboard_loading };
      default:
        return null;
    }
  };

  const source = getBankDashboard(userType);
  const loading =
    source?.loading || bankDashboard_loading || tele_bank_dashboard_loading;

  const banks = useMemo(() => {
    const rows = Array.isArray(source?.data) ? source.data : [];
    return rows.map((bank) => ({
      ...bank,
      /* Keep these numeric. The old mapping used `Number(x) || "N/A"`, which
         turned a legitimate 0 into "N/A" and made the value unusable in
         arithmetic. */
      resolvedCases: num(bank?.resolved_cases),
      unresolvedCases: num(bank?.unresolved_cases),
      progressPercent: bank?.progress_percent,
      logo: logos[bank?.name] || null,
    }));
  }, [source?.data]);

  const visibleBanks = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return banks;
    return banks.filter((b) =>
      String(b?.name || "")
        .toLowerCase()
        .includes(term),
    );
  }, [banks, search]);

  const totals = useMemo(() => {
    const odpos = banks.reduce((sum, b) => sum + num(b?.total_odpos), 0);
    const resolved = banks.reduce((sum, b) => sum + b.resolvedCases, 0);
    const unresolved = banks.reduce((sum, b) => sum + b.unresolvedCases, 0);
    const cases = resolved + unresolved;
    return {
      odpos,
      resolved,
      unresolved,
      progress: cases ? Math.round((resolved / cases) * 100) : 0,
    };
  }, [banks]);

  const getBankDetails = (e, bank_id) => {
    navigate(`/bank-dashboard/bank-details/${bank_id}`);
  };

  const getUrl = () => {
    switch (userType) {
      case "telecaller":
        return "telecaller/data-export";
      case "supervisor":
      case "agency":
        return "portfolio/bank-data-export";
      default:
        return null;
    }
  };

  const downloadPortfolio = async (e, id) => {
    try {
      setDownloadLoading(id);
      const res = await getPortfolioLink(token, id, getUrl());

      if (res?.status === 200) {
        const url = res?.data?.data?.download_url;
        if (url) {
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "portfolio.pdf");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (error) {
      console.error("Error downloading portfolio:", error);
    } finally {
      // Always clear, so a failed request can't leave the tile spinning.
      setDownloadLoading(null);
    }
  };

  const columns = [
    {
      title: "Portfolio",
      dataIndex: "name",
      key: "name",
      render: (text, record) => {
        if (record.isSkeleton) return <Skeleton active avatar paragraph={false} title={{ width: 120 }} />;
        return (
          <div className="bdash-cell-portfolio">
            <span className="bdash-cell-portfolio__logo">
              {record.logo ? (
                <img src={record.logo} alt="" />
              ) : (
                text?.substring(0, 2).toUpperCase()
              )}
            </span>
            <span className="bdash-cell-portfolio__name">
              {text || "Unnamed portfolio"}
            </span>
          </div>
        );
      }
    },
    {
      title: "Total Cases",
      key: "totalCases",
      render: (_, record) => record.isSkeleton ? <Skeleton.Input active size="small" style={{ width: 80 }} /> : formatWithCommas(record.resolvedCases + record.unresolvedCases),
    },
    {
      title: "Total ODPOS",
      dataIndex: "total_odpos",
      key: "total_odpos",
      render: (val, record) => record.isSkeleton ? <Skeleton.Input active size="small" style={{ width: 100 }} /> : <span className="bdash-cell-amount">{formatCurrency(val, format)}</span>,
    },
    {
      title: "Resolved",
      dataIndex: "resolvedCases",
      key: "resolvedCases",
      render: (val, record) => record.isSkeleton ? <Skeleton.Input active size="small" style={{ width: 60 }} /> : formatWithCommas(val),
    },
    {
      title: "Unresolved",
      dataIndex: "unresolvedCases",
      key: "unresolvedCases",
      render: (val, record) => record.isSkeleton ? <Skeleton.Input active size="small" style={{ width: 60 }} /> : formatWithCommas(val),
    },
    {
      title: "Progress",
      key: "progress",
      width: 150,
      render: (_, record) => {
        if (record.isSkeleton) return <Skeleton.Input active size="small" style={{ width: 100 }} />;
        const total = record.resolvedCases + record.unresolvedCases;
        const progress = total ? (record.resolvedCases / total) * 100 : 0;
        let color = "#e46666"; // red
        if (progress >= 70) color = "#4cb55c"; // green
        else if (progress >= 40) color = "#ffb300"; // amber
        
        return (
          <div className="bdash-cell-progress">
            <Progress
              percent={Math.round(progress)}
              strokeColor={color}
              trailColor="#eef0f7"
              size="small"
              format={(percent) => (
                <span className="bdash-cell-progress__value" style={{ color }}>
                  {percent}%
                </span>
              )}
            />
          </div>
        );
      }
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        if (record.isSkeleton) return <Skeleton.Button active size="small" shape="circle" />;
        return (
          <EllipsisMenu
            options={[
              {
                icon: <Eye />,
                label: "Show Report",
                onClick: ({ domEvent }) => getBankDetails(domEvent, record?.id),
                highlight: true,
              },
              {
                icon: <Download />,
                label: "Download Data",
                onClick: ({ domEvent }) => downloadPortfolio(domEvent, record?.id),
              },
            ]}
          />
        );
      }
    },
  ];

  return (
    <div className="bankDashboardMain">
      {/* -------------------------------- hero ------------------------------- */}
      <header className="bdash-hero">
        <div className="bdash-hero__bg" aria-hidden="true" />
        <div className="bdash-hero__content">
          <div className="bdash-hero__intro">
            <h1>Bank Dashboard</h1>
            <span className="bdash-hero__chip">
              <BankOutlined />
              {loading ? "—" : `${banks.length} portfolios`}
            </span>
          </div>

          <Input
            className="bdash-hero__search"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search portfolio"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </header>

      {/* ------------------------------ overview ----------------------------- */}
      <PortfolioOverview
        totals={totals}
        count={banks.length}
        format={format}
        loading={loading}
      />

      {/* ------------------------------- grid -------------------------------- */}
      <div style={{ background: "#fff", borderRadius: "12px", padding: "24px", border: "1px solid #eaecf6", boxShadow: "0 1px 2px rgba(33, 35, 48, 0.03)" }}>
        <div className="bdash-section" style={{ marginTop: 0 }}>
          <span className="bdash-section__label">Portfolios</span>
          <span className="bdash-section__rule" />
          <span className="bdash-section__note">
            {loading
              ? "Loading"
              : `${visibleBanks.length} of ${banks.length} shown`}
          </span>
        </div>

        <div className="bdash-table-wrapper" style={{ marginTop: "16px" }}>
        <Table
          columns={columns}
          dataSource={loading ? Array.from({ length: 5 }).map((_, i) => ({ id: `sk-${i}`, isSkeleton: true })) : visibleBanks}
          rowKey={(record, index) => record?.id ?? index}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 800 }}
          onRow={(record) => {
            if (record.isSkeleton) return {};
            return {
              onClick: (e) => getBankDetails(e, record?.id),
              style: { cursor: "pointer" }
            };
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  search
                    ? `No portfolio matches "${search}"`
                    : "No portfolios available"
                }
              />
            )
          }}
        />
        </div>
      </div>
    </div>
  );
};

export default BankDashboardPage;
